import upperFirst from 'lodash/upperFirst'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { TableCellProps } from 'react-virtualized'

import { Vehicle_TypeEnum, Vehicle } from 'src/api/gen/api'
import { VehicleStateEnum } from 'src/api/provider-agency'
import { VEHICLES_CATEGORY_ICON_MAP, VEHICLES_STATUS_COLOR_MAP } from 'src/commons/constants'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import BatterylevelIndicator from 'src/components/BatteryLevelIndicator'
import { CellLink } from 'src/components/Table/Cells'
import { FullExportableTable, ISearchBar } from 'src/components/Table/Templates/FullExportableTable'
import { dateCellRenderer, iconCellRenderer, providerCellRenderer, tagCellRenderer } from 'src/components/Table/utils'
import { useProviders, useProviderUser } from 'src/layout/hooks'

interface IProps {
  className?: string
  data: any
  isLoading?: boolean
  onClickExport?: (event: React.MouseEvent<HTMLElement>) => any
  // Search both the uuid and the VIN
  searchBar?: ISearchBar
  totalCount: number
}

function VehiclesTable(props: IProps) {
  const { data, className, isLoading, onClickExport, searchBar, totalCount } = props
  const { t } = useTranslation()
  const providers = useProviders()
  const { activeProviders } = useProviders()
  const isProviderUser = useProviderUser()

  const columns = [
    {
      label: upperFirst(t('vehicles.id')),
      width: 220,
      dataKey: 'id',
      cellRenderer: (cellProps: TableCellProps) => {
        const device = cellProps.rowData as Vehicle
        return <CellLink url={`/vehicles/${device.id}`}>{device.id}</CellLink>
      },
    },
    {
      label: upperFirst(t('vehicles.vin')),
      width: 70,
      dataKey: 'identification_number',
    },
    {
      label: upperFirst(t('vehicles.provider')),
      width: 110,
      dataKey: 'provider_id',
      cellRenderer: (cellProps: TableCellProps) => providerCellRenderer(cellProps, providers.activeProviders),
    },
    {
      label: upperFirst(t('vehicles.category')),
      width: 120,
      dataKey: 'type',
      cellRenderer: (cellProps: TableCellProps) =>
        iconCellRenderer(
          cellProps,
          (cat: Vehicle_TypeEnum) => upperFirst(t(enumsTranslationKeys.DeviceType[cat])),
          (cat: Vehicle_TypeEnum) => VEHICLES_CATEGORY_ICON_MAP[cat]
        ),
    },
    {
      label: upperFirst(t('vehicles.status')),
      width: 120,
      dataKey: 'last_known_status',
      cellRenderer: (cellProps: TableCellProps) =>
        tagCellRenderer(
          cellProps,
          (stat: VehicleStateEnum) => upperFirst(t(enumsTranslationKeys.VehicleStates[stat])),
          (stat: VehicleStateEnum) => VEHICLES_STATUS_COLOR_MAP[stat]
        ),
    },
    {
      label: upperFirst(t('vehicles.lastTelemetryDate')),
      width: 120,
      dataKey: 'last_known_timestamp',
      cellRenderer: dateCellRenderer,
    },
    {
      label: upperFirst(t('vehicles.energy')),
      width: 120,
      dataKey: 'last_known_battery_pct',
      cellRenderer: (cellProps: TableCellProps) => {
        const { cellData } = cellProps

        return (
          <div>
            <BatterylevelIndicator batteryLevel={cellData} />
          </div>
        )
      },
    },
  ]
  if (isProviderUser && activeProviders.length > 2) {
    // we remove the provider
    columns.splice(2, 1)
  }

  return (
    <FullExportableTable
      data={data}
      columns={columns}
      className={className}
      noDataMessage={t('vehicles.table.noData')}
      totalCount={totalCount}
      onClickExport={onClickExport}
      isLoading={isLoading}
      searchBar={searchBar}
    />
  )
}

export default VehiclesTable
